import { cn } from '@repo/lib/cn';
import { Pause, Play } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { VideoPlayerOverlayProps } from '.';

import { useMedia } from '..';

export function VideoPlayerOverlay(videoPlayerOverlayProps: VideoPlayerOverlayProps) {
  const { autoHide = true, className, onClick, ...props } = videoPlayerOverlayProps;
  const { isPlaying, element, togglePlay } = useMedia();
  const [showControls, setShowControls] = useState<boolean>(true);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    if (autoHide) {
      hideTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 1000);
    }
  };

  useEffect(() => {
    if (isPlaying) {
      resetHideTimeout();
    } else {
      setShowControls(true);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    }

    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [isPlaying, autoHide]);

  useEffect(() => {
    const elementCurrent = element.current;
    if (!elementCurrent) return;
    const controller = new AbortController();
    const handleMouseMove = () => {
      setShowControls(true);
      resetHideTimeout();
    };

    elementCurrent.addEventListener('mousemove', handleMouseMove, {
      signal: controller.signal,
    });

    return () => controller.abort();
  }, []);

  return (
    <div className={cn('pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-300', !isPlaying || showControls ? 'opacity-100' : 'opacity-0')}>
      <button
        type={'button'}
        onClick={(e) => {
          e.stopPropagation();
          togglePlay();
          onClick?.(e);
        }}
        className={cn(
          'pointer-events-auto flex h-11 w-11 items-center justify-center rounded-2xl border border-white/30 bg-white/20 text-white backdrop-blur-sm transition-all duration-200 hover:bg-white/30',
          className
        )}
        {...props}
      >
        {isPlaying ? <Pause className="size-4" /> : <Play className="ml-0.5 size-4" />}
      </button>
    </div>
  );
}
